import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { formatDate } from '@angular/common';
import { SessionsService } from 'src/app/services/sessions.service';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';

@Component({
  selector: 'app-edit-session',
  templateUrl: './edit-session.component.html',
  styleUrls: ['./edit-session.component.scss']
})
export class EditSessionComponent implements OnInit {

  sessionForm: FormGroup;
  eventName: string = '';
  sessionId: number;
  idEvent: number;
  typeDatepicker: string = null;
  originalDateParts: { year: number, month: number, day: number } = null;
  isTemporarySession: boolean = false;
  temporarySessionIndex: number = -1;
  existingSessions: any[] = [];
  sessionActive: boolean = true;
  togglingActive: boolean = false;
  isSaving: boolean = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EditSessionComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private sessionsService: SessionsService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.eventName = this.data.eventName || '';
      this.sessionId = this.data.sessionId;
      this.idEvent = this.data.idEvent;
      this.existingSessions = this.data.existingSessions || [];

      const sessionData = this.data.sessionData;

      if (sessionData?._isTemporary && this.data.temporarySessionIndex !== undefined) {
        this.isTemporarySession = true;
        this.temporarySessionIndex = this.data.temporarySessionIndex;
      }

      this.sessionActive = sessionData?.active ?? true;

      let sessionDate: Date | null = null;
      let dateString = '';
      let timeString = '0:00';

      if (sessionData?.start_date) {
        if (typeof sessionData.start_date === 'string' && sessionData.start_date.includes(' ')) {
          const parts = sessionData.start_date.split(' ');
          const dateParts = parts[0].split('-');
          const timeParts = parts[1].split(':');

          this.originalDateParts = {
            year: parseInt(dateParts[0]),
            month: parseInt(dateParts[1]) - 1,
            day: parseInt(dateParts[2])
          };

          const hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);

          sessionDate = new Date(
            this.originalDateParts.year,
            this.originalDateParts.month,
            this.originalDateParts.day,
            hours,
            minutes,
            0,
            0
          );

          dateString = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
          timeString = parts[1].substring(0, 5);
        } else {
          const utcDate = new Date(sessionData.start_date);

          const utcYear = utcDate.getUTCFullYear();
          const utcMonth = utcDate.getUTCMonth();
          const utcDay = utcDate.getUTCDate();
          const utcHours = utcDate.getUTCHours();
          const utcMinutes = utcDate.getUTCMinutes();

          this.originalDateParts = {
            year: utcYear,
            month: utcMonth,
            day: utcDay
          };

          // Crear fecha LOCAL con los valores UTC para que datetime-selector muestre correctamente
          sessionDate = new Date(utcYear, utcMonth, utcDay, utcHours, utcMinutes, 0, 0);
          dateString = formatDate(sessionDate, 'dd/MM/yyyy', 'es');
          timeString = formatDate(sessionDate, 'HH:mm', 'es');
        }
      }

      this.sessionForm = this.fb.group({
        date: [sessionDate, Validators.required],
        dateString: [''],
        time: [timeString, Validators.required],
        allDay: [sessionData?.all_day || false]
      });
    } else {
      // Si no hay datos, crear formulario vacío
      this.sessionForm = this.fb.group({
        date: [null, Validators.required],
        dateString: [''],
        time: ['0:00', Validators.required],
        allDay: [false]
      });
    }
  }

  openDatePicker(type: string): void {
    if (this.typeDatepicker === type) {
      this.typeDatepicker = null;
      return;
    }
    this.typeDatepicker = type;
  }

  saveDate(event: any): void {
    if (event) {
      const date = event instanceof Date ? event : new Date(event);

      const localHours = date.getHours();
      const localMinutes = date.getMinutes();
      const timeString = `${String(localHours).padStart(2, '0')}:${String(localMinutes).padStart(2, '0')}`;

      this.originalDateParts = {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate()
      };

      const dateString = `${String(this.originalDateParts.day).padStart(2, '0')}/${String(this.originalDateParts.month + 1).padStart(2, '0')}/${this.originalDateParts.year}`;

      this.sessionForm.patchValue({
        date: date,
        dateString: dateString,
        time: timeString
      });
    }
    this.typeDatepicker = null;
  }

  toggleAllDay(): void {
    const allDay = this.sessionForm.get('allDay').value;
    if (allDay) {
      this.sessionForm.patchValue({ time: '0:00' });
    }
  }

  private hasDuplicateSession(formattedDate: string): boolean {
    const candidateDate = new Date(formattedDate);
    const candidateDateStr = formatDate(candidateDate, 'yyyy-MM-dd', 'en-US');
    const candidateTimeStr = formatDate(candidateDate, 'HH:mm', 'en-US');

    return this.existingSessions.some(existing => {
      if (this.isTemporarySession) {
        if (existing._isTemporary && existing._temporaryIndex === this.temporarySessionIndex) {
          return false;
        }
      } else if (existing.id === this.sessionId) {
        return false;
      }

      const existingDate = new Date(existing.start_date);
      const existingDateStr = formatDate(existingDate, 'yyyy-MM-dd', 'en-US');
      const existingTimeStr = formatDate(existingDate, 'HH:mm', 'en-US');

      return existingDateStr === candidateDateStr && existingTimeStr === candidateTimeStr;
    });
  }

  saveSession(): void {
    if (this.sessionForm.invalid || this.isSaving) {
      return;
    }

    const formValue = this.sessionForm.value;
    const [hours, minutes] = formValue.time.split(':').map(Number);

    const sessionDate = new Date(
      this.originalDateParts.year,
      this.originalDateParts.month,
      this.originalDateParts.day,
      hours,
      minutes,
      0,
      0
    );

    const formattedDate = formatDate(sessionDate, 'yyyy-MM-dd HH:mm:ss', 'en-US');

    if (this.hasDuplicateSession(formattedDate)) {
      const dateFormatted = formatDate(sessionDate, 'dd/MM/yyyy', 'es');
      const timeFormatted = formatDate(sessionDate, 'HH:mm', 'en-US');
      this.dialog.open(InfoCardComponent, {
        panelClass: ['info-dialog'],
        disableClose: false,
        data: {
          icon: true,
          text: `Ya existe una sesión el ${dateFormatted} a las ${timeFormatted}h. No se pueden guardar sesiones duplicadas en el mismo horario.`,
          warning: true,
          btnCancel: false
        }
      });
      return;
    }

    // Si es una sesión temporal, actualizar en el observable
    if (this.isTemporarySession && this.temporarySessionIndex !== -1) {
      const updatedSession = {
        start_date: formattedDate,
        all_day: formValue.allDay,
        _isTemporary: true
      };

      this.sessionsService.updateSessionInObservable(this.temporarySessionIndex, updatedSession);
      this.dialogRef.close({ success: true, action: 'update', isTemporary: true });
      return;
    }

    // Si es una sesión real, actualizar en la API
    const payload = {
      start_date: formattedDate,
      all_day: formValue.allDay
    };

    const saveAndClose = (session: any) => {
      this.dialogRef.close({ success: true, action: 'update', session });
    };

    const updateSession$ = this.sessionsService.updateSessionById(payload, this.sessionId);
    const toggleActive$ = this.sessionsService.toggleSessionActive(this.sessionId, this.sessionActive);

    this.isSaving = true;
    updateSession$.subscribe({
      next: (result) => {
        if (this.sessionActive !== this.data.sessionData?.active) {
          toggleActive$.subscribe({
            next: () => saveAndClose(result),
            error: () => saveAndClose(result)
          });
        } else {
          saveAndClose(result);
        }
      },
      error: () => {
        this.isSaving = false;
        this.dialog.open(InfoCardComponent, {
          panelClass: ['info-dialog'],
          data: {
            icon: true,
            text: 'Ha ocurrido un error al guardar la sesión. Por favor, inténtalo de nuevo.',
            warning: true,
            btnCancel: false
          }
        });
      }
    });
  }

  deleteSession(): void {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: true,
        text: '¿Estás seguro de que desea eliminar la sesión?',
        warning: true,
        btnCancel: true
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        // Si es una sesión temporal, eliminar del observable
        if (this.isTemporarySession && this.temporarySessionIndex !== -1) {
          this.sessionsService.deleteSessionFromObservable(this.temporarySessionIndex);
          this.dialogRef.close({ success: true, action: 'delete', isTemporary: true });
          return;
        }

        // Si es una sesión real, eliminar de la API
        this.sessionsService.deleteSession(this.sessionId, this.idEvent).subscribe({
          next: (result) => {
            this.dialogRef.close({ success: true, action: 'delete', sessionId: this.sessionId });
          },
          error: (error) => {
            this.dialogRef.close({ success: false, action: 'delete', error: error.error.message });
          }
        });
      }
    });
  }

  toggleActive(): void {
    // Solo actualiza el estado local; la llamada al servicio se realiza al guardar
  }

  close(): void {
    this.dialogRef.close();
  }
}
